import { Program, Channel, Picture } from '../../../interfaces';
import { Link } from 'react-router-dom';
import i18n from '../../../i18n';
import { getLockedMessage } from '../../../utils/tvUtils';
import { hasPicture, getImageUrlDimensions } from '../../../utils/altiboxassetUtils';
import { imageScaleValues } from '../../../config';
import imageMissing from './programbilde_mangler_v2.png';
import ProgramHeader from './PlaybillComponents/ProgramHeader';
import Badge from '../../Badge';
import { useMemo } from 'react';
import { useProgram } from '../../../hooks/useProgram';
import { routes } from '../../../routes/route-utils';
import { useAuth } from '../../../utils/auth-utils';
import channelPermissions from '../../../utils/channelPermissions';

interface Props {
  channel: Channel;
  program: Program;
  isRecording: boolean;
  markers: JSX.Element;
  filterActive: boolean;
}

export default function LiveProgram({ channel, program: propsProgram, isRecording, markers, filterActive }: Props) {
  const auth = useAuth();
  const program = useProgram(propsProgram);
  const route = routes.live({ channelId: program.channelid });
  const permission = channelPermissions(channel, auth);
  const canPlay = program.canCatchup && !permission.locked && !auth.isGuest;

  let className = 'tvGuideplaybillProgram live' + (filterActive ? ' filter-active' : '');

  return (
    <li className={className}>
      <ProgramCard
        isRecording={isRecording}
        liveUrl={route.pathname + route.search}
        channel={channel}
        picture={program.picture}
        name={program.name}
        progress={program.getProgress()}
      />
      <ProgramHeader
        className="live"
        id={program.id}
        name={program.name}
        channelId={program.channelid}
        startTime={program.startTime}
        episodeNumber={program.episodeNumber}
        extensionInfo={program.extensionInfo}
        canPlay={canPlay}
        markers={
          <PlayAndMarkers
            channelIsLocked={permission.locked}
            isGuest={auth.isGuest}
            markers={markers}
            liveUrl={route.pathname + route.search}
          />
        }
      />
    </li>
  );
}

interface ProgramCardProps {
  isRecording: boolean;
  liveUrl: string;
  channel: Channel;
  picture: Picture;
  name: string;
  progress: number;
}

function ProgramCard({ isRecording, liveUrl, channel, picture, name, progress }: ProgramCardProps) {
  const auth = useAuth();
  const permission = channelPermissions(channel, auth);

  if (permission.locked) {
    return (
      <div className="image">
        {isRecording && <Badge className="badge" text={i18n.t<string>('is recording')} type={'primary'} />}
        <LiveImage channel={channel} picture={picture} name={name} progress={progress} />
      </div>
    );
  }

  return (
    <Link className="image" to={{ pathname: liveUrl }}>
      {isRecording && <Badge className="badge" text={i18n.t<string>('is recording')} type={'primary'} />}
      <LiveImage channel={channel} picture={picture} name={name} progress={progress} />
    </Link>
  );
}

interface PlayAndMarkersProps {
  channelIsLocked: boolean;
  isGuest: boolean;
  markers: JSX.Element;
  liveUrl: string;
}

function PlayAndMarkers({ channelIsLocked, isGuest, markers, liveUrl }: PlayAndMarkersProps) {
  if (channelIsLocked || isGuest) {
    return null;
  }

  return (
    <>
      {markers}
      <li className="from-start">
        <Link to={{ pathname: liveUrl, state: { prevPath: window.location.pathname, reloadPage: true } }}>z</Link>
      </li>
    </>
  );
}

interface LiveImageProps {
  channel: Channel;
  picture: Picture;
  name: string;
  progress: number;
}

function LiveImage({ channel, picture, name, progress }: LiveImageProps) {
  const auth = useAuth();
  const permission = channelPermissions(channel, auth);

  const liveProgramPictureUrl = useMemo(() => {
    return hasPicture(picture) ? getImageUrlDimensions(picture.deflate, imageScaleValues.tvGuide) : imageMissing;
  }, [picture]);

  return (
    <>
      <img
        className="noselect"
        alt={name}
        src={liveProgramPictureUrl}
        loading="lazy"
        onError={(e) => {
          e.currentTarget.src = imageMissing;
        }}
      />

      {permission.locked ? (
        <div className="noselect overlay lockedMessage">
          <span>
            <span className="aib-icon">p</span>
            <br />
            {getLockedMessage(channel)}
          </span>
        </div>
      ) : (
        <div className="noselect overlay playIcon">
          <span className="aib-icon">{auth.isGuest ? '' : 'Ă'}</span>
        </div>
      )}
      <div className="noselect overlay gradient" />
      <div className="noselect progressWrapper">
        <progress value={progress} />
      </div>
    </>
  );
}
